import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Layers,
  FileText,
  HelpCircle,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Loader,
  ChevronRight,
  Video,
  FileQuestion,
  Clock,
  Users,
  CheckCircle,
} from "lucide-react";
import { coursesApi, modulesApi, showSuccess, showError } from "../lib";

interface Course {
  _id: string;
  title: string;
  description: string;
  thumbnail?: string;
  status: string;
  instructor: { _id: string; name: string; email: string };
}

interface Module {
  _id: string;
  course: string;
  title: string;
  description: string;
  order: number;
}

interface Lesson {
  _id: string;
  module: string;
  title: string;
  content: string;
  videoUrl?: string;
  duration: number;
  order: number;
}

const AdminCourseContentPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"modules" | "lessons" | "quizzes">("modules");

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchModules();
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const result = await coursesApi.getAllCourses({ limit: 100 });
      if (result.success) {
        setCourses(result.courses || result.data);
        if (result.courses?.length > 0 && !selectedCourse) {
          setSelectedCourse(result.courses[0]);
        }
      }
    } catch (err: any) {
      showError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    if (!selectedCourse) return;
    try {
      const result = await modulesApi.getAllModules(selectedCourse._id);
      if (result.success) {
        setModules(result.data || []);
      }
    } catch (err: any) {
      console.error("Failed to load modules:", err);
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    if (!confirm("Are you sure you want to delete this module?")) return;
    try {
      await modulesApi.deleteModule(moduleId);
      showSuccess("Module deleted successfully");
      fetchModules();
    } catch (err: any) {
      showError(err.message || "Failed to delete module");
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm("Are you sure you want to delete this lesson?")) return;
    try {
      await modulesApi.deleteLesson(lessonId);
      showSuccess("Lesson deleted successfully");
    } catch (err: any) {
      showError(err.message || "Failed to delete lesson");
    }
  };

  const tabs = [
    { id: "modules", label: "Modules", icon: Layers },
    { id: "lessons", label: "Lessons", icon: FileText },
    { id: "quizzes", label: "Quizzes", icon: HelpCircle },
  ];

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Course Content Management</h1>
          <p className="text-sm text-gray-500">Manage modules, lessons, and quizzes across all courses</p>
        </div>

        {/* Course Selector */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Course</label>
          <select
            value={selectedCourse?._id || ""}
            onChange={(e) => {
              const course = courses.find((c) => c._id === e.target.value);
              setSelectedCourse(course || null);
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
          >
            <option value="">Select a course</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>
                {course.title} - {course.instructor?.name || "Unknown Instructor"}
              </option>
            ))}
          </select>
        </div>

        {selectedCourse && (
          <>
            {/* Course Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{selectedCourse.title}</h3>
                  <p className="text-sm text-gray-500">{selectedCourse.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> Instructor: {selectedCourse.instructor?.name}
                    </span>
                    <span className={`flex items-center gap-1 ${selectedCourse.status === "published" ? "text-green-600" : "text-yellow-600"}`}>
                      <CheckCircle className="w-3 h-3" /> {selectedCourse.status}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/course/${selectedCourse._id}`}
                  target="_blank"
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" /> View Course
                </Link>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-gray-200 mb-6">
              <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.id === "modules" && (
                      <span className="ml-1 text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                        {modules.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="w-6 h-6 text-primary animate-spin" />
                  </div>
                ) : activeTab === "modules" ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-900">Modules ({modules.length})</h3>
                    </div>
                    {modules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p>No modules in this course yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {modules.map((module, index) => (
                          <div
                            key={module._id}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                          >
                            <span className="w-6 h-6 bg-primary/10 text-primary text-xs font-medium rounded-full flex items-center justify-center">
                              {index + 1}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{module.title}</p>
                              <p className="text-sm text-gray-500 truncate">{module.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDeleteModule(module._id)}
                                className="p-2 text-gray-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : activeTab === "lessons" ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-900">Lessons</h3>
                    </div>
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p>Manage lessons from the module editor</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-900">Quizzes</h3>
                    </div>
                    <div className="text-center py-8 text-gray-500">
                      <FileQuestion className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p>No quizzes in this course yet</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!loading && !selectedCourse && (
          <div className="bg-white rounded-xl border border-gray-200 text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Select a course to manage its content</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminCourseContentPage;